import { Suspense, useMemo, useState } from "react";

import cube from "@cubejs-client/core";
import { CubeProvider } from "@cubejs-client/react";
import WebSocketTransport from "@cubejs-client/ws-transport";
import { cn, type Provider } from "@stacklet/ui";
import { handleComponentError } from "initSentry";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useLocation, useSearchParams } from "react-router";

import { ApiExplorerSync } from "app/components/ApiExplorerSync"; // avoiding barrel export to satisfy relay
import { ColorSchemeSync } from "app/components/ColorSchemeSync"; // avoiding barrel export to satisfy relay
import { CircularFallback, ErrorFallback } from "app/components/fallbacks";
import { Help } from "app/components/Help";
import { HelpDrawer } from "app/components/HelpDrawer/HelpDrawer";
import { HelpDrawerProvider } from "app/components/HelpDrawer/HelpDrawerContext";
import { SideBarWrapper } from "app/components/layout/sidebar/SideBarWrapper"; // avoiding barrel export to satisfy relay
import { paths } from "app/constants/paths";
import { AlertProvider, HelpContext, SideBarContext } from "app/contexts";
import { ProviderContext } from "app/contexts/ProviderContext";
import { UserContextProvider } from "app/contexts/UserContext"; // avoiding barrel export to satisfy relay
import {
  useInitAnalytics,
  useLocationChange,
  useSentry,
  useStickyState,
} from "app/hooks";
import { useEnabledDashboardsFilterElement } from "app/hooks/feature-flags/useEnabledDashboards/useEnabledDashboardsFilterElement";
import { getAuthToken } from "app/utils/auth";
import { useCubejsConfig } from "app/views/dashboards/hooks/useCubejsConfig";

import { AppRoutesLayout } from "./AppRoutesLayout";
import "./index.css";
// stacklet/ui css
import "@stacklet/ui/dist/ui.css";
import type { AppQuery } from "__generated__/AppQuery.graphql";

export function App() {
  const savedProvider = localStorage.getItem("provider");
  const selectedProvider = savedProvider
    ? JSON.parse(savedProvider)
    : ("aws" as Provider);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [provider, setProvider] = useState<Provider>(selectedProvider);

  const isHelpOpen = searchParams.get("help") === "true";

  const filterElement = useEnabledDashboardsFilterElement(provider);

  const data = useLazyLoadQuery<AppQuery>(
    graphql`
      query AppQuery($filterElement: FilterElementInput) {
        ...ApiExplorerSync_query
        ...AppRoutesLayout_accounts
        ...ColorSchemeSync_query
        platform {
          cloudCustodianVersion
          version
        }
        ...SideBarWrapper_query @arguments(filterElement: $filterElement)
        ...UserContext_platform
      }
    `,
    { filterElement },
  );

  const { version, cloudCustodianVersion } = data.platform;

  useInitAnalytics(version, cloudCustodianVersion);
  useSentry(version);
  const apiUrl = useCubejsConfig();

  const [helpCollapsed, setHelpCollapsed] = useState(true);
  const [helpPages, setHelpPages] = useState<string[]>([]);
  const [sideBarCollapsed, setSideBarCollapsed] = useStickyState(
    false,
    "isSideBarCollapsed",
  );

  useLocationChange((loc, prevLoc) => {
    if (loc !== prevLoc) {
      setHelpCollapsed(true);
    }
  });

  function handleProviderChange(provider: Provider) {
    setProvider(provider);
  }

  const cubeApi = useMemo(() => {
    if (apiUrl) {
      return cube(() => getAuthToken(), {
        transport: new WebSocketTransport({
          apiUrl,
        }),
        apiUrl,
      });
    }
  }, [apiUrl]);

  const isApiRoute = location.pathname === paths.api;

  const appShell = (
    <div className="flex flex-grow">
      <UserContextProvider query={data}>
        <ProviderContext.Provider value={provider}>
          <SideBarContext.Provider
            value={{
              collapsed: sideBarCollapsed,
              toggleCollapsed: () => setSideBarCollapsed(!sideBarCollapsed),
            }}
          >
            <HelpContext.Provider
              value={{
                collapsed: helpCollapsed,
                expand: () => setHelpCollapsed(false),
                collapse: () => setHelpCollapsed(true),
                toggle: () => setHelpCollapsed(!helpCollapsed),
                pageNames: helpPages,
                setPageNames: (pageNames) => setHelpPages(pageNames),
              }}
            >
              <HelpDrawerProvider>
                <AlertProvider>
                  <SideBarWrapper
                    onProviderChange={handleProviderChange}
                    query={data}
                  />
                  <div
                    className={cn(
                      "flex h-screen flex-1 flex-col",
                      isApiRoute ? "overflow-hidden" : "overflow-auto",
                    )}
                  >
                    <main
                      className={cn(
                        "flex flex-grow flex-col px-xl",
                        !isApiRoute ? "mb-lg" : "",
                        sideBarCollapsed ? "w-[calc(100%-64px)]" : "w-full",
                      )}
                    >
                      <ErrorBoundary
                        FallbackComponent={ErrorFallback}
                        onError={handleComponentError}
                        resetKeys={[location.pathname]}
                      >
                        <Suspense fallback={<CircularFallback />}>
                          <div
                            className={cn(
                              "flex-grow transition-all duration-300 ease-in-out",
                              isHelpOpen ? "mr-[20rem]" : "",
                            )}
                          >
                            <div
                              className={cn(
                                "flex-grow",
                                !helpCollapsed
                                  ? "min-[1440px]:mr-[300px] 2xl:mr-[500px]"
                                  : "",
                              )}
                            >
                              <HelpDrawer />
                              <AppRoutesLayout queryRef={data} />
                            </div>
                          </div>
                          <Help pageNames={helpPages} />
                        </Suspense>
                      </ErrorBoundary>
                    </main>
                  </div>
                </AlertProvider>
              </HelpDrawerProvider>
            </HelpContext.Provider>
          </SideBarContext.Provider>
        </ProviderContext.Provider>
      </UserContextProvider>
    </div>
  );

  return (
    <>
      {/* Mounts as soon as AppQuery resolves — independent of the async Cube
          config fetch (cubeApi) — so the server color scheme reconciles right
          away instead of waiting on a request it does not depend on. */}
      <ColorSchemeSync queryRef={data} />
      <ApiExplorerSync queryRef={data} />
      {cubeApi ? (
        <CubeProvider cubeApi={cubeApi}>{appShell}</CubeProvider>
      ) : (
        appShell
      )}
    </>
  );
}
